import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, FileText, Trash2, ChevronLeft, Clock, Search, CalendarClock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { appointmentStatus, toneClasses } from "@/lib/appointment";

const STATUS_LABEL = {
  history: "History taken",
  questions: "Questions",
  provisional: "Provisional Dx",
  investigations: "Investigations",
  final: "Final Dx",
  care_plan: "Care Plan",
  follow_up: "Follow-up",
};

export default function CaseList() {
  const { role } = useParams();
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const load = async () => {
    try {
      setCases(await api.listCases());
    } catch (e) {
      toast.error("Could not load cases");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (id, e) => {
    e.stopPropagation();
    try {
      await api.deleteCase(id);
      toast.success("Case deleted");
      load();
    } catch (err) {
      toast.error("Could not delete case");
    }
  };

  const q = query.trim().toLowerCase();
  const filtered = q
    ? cases.filter((c) =>
        [c.patient?.name, c.chief_complaint, c.final_diagnosis?.name, c.impression]
          .filter(Boolean)
          .some((t) => String(t).toLowerCase().includes(q))
      )
    : cases;

  return (
    <div className="min-h-screen px-6 pb-24 pt-10">
      <div className="mx-auto max-w-4xl">
        <button
          data-testid="back-to-roles"
          onClick={() => navigate("/")}
          className="mb-6 inline-flex items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" /> Change role
        </button>

        <div className="mb-8 flex items-end justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-primary">
              {role === "patient" ? "Patient portal" : "Clinician workspace"}
            </p>
            <h1 className="font-heading text-3xl font-semibold text-foreground sm:text-4xl">
              Patient Cases
            </h1>
          </div>
          <Button
            data-testid="new-case-btn"
            onClick={() => navigate(`/${role}/cases/new`)}
            className="gap-2"
          >
            <Plus className="h-4 w-4" /> New Case
          </Button>
        </div>

        {!loading && cases.length > 0 && (
          <div className="relative mb-5">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="case-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, complaint or diagnosis…"
              className="pl-9"
            />
          </div>
        )}

        {loading ? (
          <p className="text-muted-foreground">Loading…</p>
        ) : cases.length === 0 ? (
          <Card className="flex flex-col items-center gap-3 border-dashed p-12 text-center">
            <FileText className="h-10 w-10 text-muted-foreground" strokeWidth={1.5} />
            <p className="font-heading text-lg font-semibold">No cases yet</p>
            <p className="max-w-sm text-sm text-muted-foreground">
              Start a new case to walk through history, diagnosis, treatment and follow-up.
            </p>
            <Button data-testid="empty-new-case-btn" onClick={() => navigate(`/${role}/cases/new`)} className="mt-2 gap-2">
              <Plus className="h-4 w-4" /> Create first case
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {filtered.length === 0 && (
              <p data-testid="no-search-results" className="text-sm text-muted-foreground">
                No cases match “{query}”.
              </p>
            )}
            {filtered.map((c) => (
              <Card
                key={c.id}
                data-testid={`case-card-${c.id}`}
                onClick={() => navigate(`/${role}/cases/${c.id}`)}
                className="flex cursor-pointer items-center justify-between gap-4 p-5 transition-shadow hover:shadow-md"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="truncate font-heading text-lg font-semibold text-foreground">
                      {c.patient?.name || "Unnamed patient"}
                    </h3>
                    <Badge variant="secondary" className="shrink-0">
                      {STATUS_LABEL[c.status] || c.status}
                    </Badge>
                    {(() => {
                      const s = appointmentStatus(c.next_appointment);
                      return s && (s.tone === "overdue" || s.tone === "due" || s.tone === "soon") ? (
                        <span
                          data-testid={`appointment-badge-${c.id}`}
                          className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${toneClasses[s.tone]}`}
                        >
                          <CalendarClock className="h-3 w-3" /> {s.label}
                        </span>
                      ) : null;
                    })()}
                  </div>
                  <p className="mt-0.5 truncate text-sm text-muted-foreground">
                    {c.chief_complaint}
                  </p>
                  <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    {new Date(c.updated_at).toLocaleString()}
                  </p>
                </div>
                <button
                  data-testid={`delete-case-${c.id}`}
                  onClick={(e) => remove(c.id, e)}
                  className="shrink-0 rounded-md p-2 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
